import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import {
  Avatar,
  GridLayout,
  GridLayoutItem,
} from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import {
  ActiveInactiveRoom,
  deleteRoom,
  getAllRooms,
} from "./services/room.services";
import { clearRoomID, setRoomID } from "./roomSlice";
import { Tooltip } from "@mui/material";
import IconButton from "../../components/common/IconButton";
import ExpandableText from "../../components/common/ExpandableText";
import { useNavigate } from "react-router-dom";
import { FiEdit } from "react-icons/fi";

const Room: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(getAllRooms());
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const handleCreate = () => {
    navigate("/room/create");
  };

  return (
    <>
      <DeleteRoomDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Room List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              {"Add New Room"}
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <RoomGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const RoomGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const RoomList = useAppSelector((state) => state.room.RoomList);
  const loading = useAppSelector((state) => state.room.loading);

  useEffect(() => {
    dispatch(getAllRooms());
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? RoomList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: any) => {
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteRoom"));
      dispatch(setRoomID(ID));
    };

    const handleEdit = (ID: number) => {
      navigate("/Room/edit", { state: { RoomID: ID } });
    };

    return (
      <td
        {...props.tdProps}
        style={props.tdProps.style}
        className={`action-td ${props.className}`}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(props.dataItem.ID)}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 9 || props.index === 10 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyActiveInactiveCommandCell = (props: any) => {
    const [IsActive, setIsActive] = useState<boolean>(
      props.dataItem.IsActive ?? false,
    );

    const handleActiveInactive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(ActiveInactiveRoom(data)).then((response: any) => {
        if (response.payload?.data?.Status === 200) {
          dispatch(getAllRooms());
        } else {
          dispatch(getAllRooms());
        }
      });
    };

    return (
      <td
        {...props.tdProps}
        style={{ textAlign: "center", ...props.tdProps.style }}
        className={props.className}
      >
        <Switch
          disabled={isEditAccess ? false : true}
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInactive}
          size="small"
        />
      </td>
    );
  };

  const ImageCell = (props: GridCellProps) => (
    <td style={{ textAlign: "center", ...props.style }}>
      <Avatar
        style={{
          cursor: "pointer",
          width: "100%",
          height: "100%",
          flexBasis: 45,
          backgroundColor: "white",
        }}
        rounded={"medium"}
        type={"image"}
      >
        <Tooltip
          placement="left"
          title={
            props.dataItem?.Image ? (
              <img
                style={{
                  padding: "5px",
                  backgroundColor: "white",
                  height: 150,
                }}
                src={props.dataItem?.Image}
                alt="Room Image"
                width="100%"
                onClick={() => window.open(props.dataItem?.Image)}
              />
            ) : (
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "#f5f5f5",
                  color: "#666",
                  textAlign: "center",
                  width: "150px",
                }}
              >
                No Image Available
              </div>
            )
          }
        >
          {props.dataItem?.Image ? (
            <img
              style={{ padding: "10px", height: "60px", objectFit: "cover" }}
              src={props.dataItem?.Image}
              alt="Room Image"
              onClick={() => window.open(props.dataItem?.Image)}
            />
          ) : (
            <div
              style={{
                width: "60px",
                height: "60px",
                backgroundColor: "#f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                color: "#999",
                fontSize: "12px",
                padding: "5px",
                textAlign: "center",
              }}
            >
              No Image
            </div>
          )}
        </Tooltip>
      </Avatar>
    </td>
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {RoomList && RoomList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          filterable={true}
          filter={filter}
          className="responsive-table"
          data={
            filter
              ? filterBy(RoomList, filter).slice(
                  page.skip,
                  page.take + page.skip,
                )
              : RoomList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={RoomList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="RoomTitle"
            title="Room Title"
            width={200}
            cells={{
              data: (props: any) => (
                <td>
                  <div className="hoverableName">
                    {isEditAccess ? (
                      <div
                        className="hoverableName"
                        onClick={() =>
                          navigate("/room/edit", {
                            state: { RoomID: props.dataItem?.ID },
                          })
                        }
                      >
                        {props.dataItem?.RoomTitle}
                      </div>
                    ) : (
                      <div className="normalName">
                        {props.dataItem?.RoomTitle}
                      </div>
                    )}
                  </div>
                </td>
              ),
            }}
          />
          <Column
            field="Description"
            title="Description"
            width={300}
            cells={{
              data: (props) => (
                <ExpandableText text={props.dataItem.Description} />
              ),
            }}
          />
          <Column field="RoomCategory" title="Category" width={150} />
          <Column field="Rate" title="Rate" width={120} />
          <Column field="SqFoot" title="Size" width={120} />
          <Column field="BedSize" title="Bed Size" width={130} />
          <Column field="Adults" title="Capacity" width={120} />
          <Column field="RoomViewType" title="View Type" width={130} />
          <Column
            field="Image"
            title="Image"
            width={100}
            filterable={false}
            cells={{ data: ImageCell }}
          />
          <Column
            field="ID"
            title="Status"
            cells={{ data: MyActiveInactiveCommandCell }}
            width={"90px"}
            filterable={false}
            locked={true}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cells={{ data: MyEditCommandCell }}
              width={"110px"}
              locked={true}
              filterable={false}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteRoomDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const RoomID = useAppSelector((state) => state.room.RoomID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteRoom(ID));
      if (response.payload.Data.Status === 200) {
        dispatch(getAllRooms());
        dispatch(closeDialog());
        dispatch(clearRoomID());
      } else {
        dispatch(getAllRooms());
        dispatch(closeDialog());
        dispatch(clearRoomID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteRoom" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Room"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this room?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(RoomID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Room;
